import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type RoundRobinAssignee,
  type SarvamDispositionMappingRow,
  type SarvamCampaignProcedureMappingRow,
  type SarvamDoctorMappingRow,
  type SarvamVoiceAiSettings,
} from "@/api/hospitalAdmin";
import { AESTHETIC_PROCEDURE_OPTIONS } from "@/lib/aestheticProcedure";
import type { Doctor } from "@/types/types";
import { CRM_LEAD_SOURCES } from "@/lib/ipdLeadSources";
import {
  LEAD_STATUS_OPTIONS,
  LEAD_SUBSTATUS_OPTIONS_BY_STATUS,
  formatLeadStatusLabel,
  formatLeadSubStatusLabel,
} from "@/lib/crmLeadStyles";
import type { LeadStatus, LeadSubStatus } from "@/api/leads";
import { toast } from "sonner";
import {
  callingWindowTimesFromSettings,
  defaultSarvamCallingWindowTimes,
} from "@/lib/sarvamCallingWindow";

type Props = {
  salesAssignees: RoundRobinAssignee[];
};

const REMINDER_AGENT_PURPOSE = "APPOINTMENT_REMINDER";

const SARVAM_PROCEDURE_NAME_SUGGESTIONS = [
  ...AESTHETIC_PROCEDURE_OPTIONS,
  "Bariatric Surgery",
  "Allurion Gastric Balloon",
];

function pickCrmSarvamAgent(
  agents: SarvamVoiceAiSettings["agents"] | undefined,
  defaultAgentId?: string | null
) {
  if (!agents?.length) return undefined;
  const defaultId = defaultAgentId?.trim();
  if (defaultId) {
    const match = agents.find((a) => a.sarvamAppId === defaultId);
    if (match && match.purpose !== REMINDER_AGENT_PURPOSE) return match;
  }
  return (
    agents.find((a) => a.enabled && a.purpose !== REMINDER_AGENT_PURPOSE) ??
    agents.find((a) => a.purpose !== REMINDER_AGENT_PURPOSE) ??
    agents[0]
  );
}

export function SarvamVoiceAiSettingsCard({ salesAssignees }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<SarvamVoiceAiSettings>({
    queryKey: ["sarvam-voice-ai-settings"],
    queryFn: () => hospitalAdminApi.getSarvamVoiceAiSettings(),
  });

  const { data: campaignOptions = [] } = useQuery<string[]>({
    queryKey: ["sarvam-campaign-options"],
    queryFn: () => hospitalAdminApi.listSarvamCampaignOptions(),
  });

  const [enabled, setEnabled] = useState(false);
  const [rolloutMode, setRolloutMode] = useState<'TEST' | 'LIVE'>('TEST');
  const [agentEnabled, setAgentEnabled] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<string[]>([]);
  const [staffIds, setStaffIds] = useState<string[]>([]);
  const [callUnassigned, setCallUnassigned] = useState(true);
  const [callDelayMinutes, setCallDelayMinutes] = useState("0");
  const [startTime, setStartTime] = useState(defaultSarvamCallingWindowTimes().startTime);
  const [endTime, setEndTime] = useState(defaultSarvamCallingWindowTimes().endTime);
  const [agentAppId, setAgentAppId] = useState("");
  const [agentVersion, setAgentVersion] = useState("1");
  const [connectionId, setConnectionId] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [customCampaign, setCustomCampaign] = useState("");
  const [dispositionMappings, setDispositionMappings] = useState<SarvamDispositionMappingRow[]>([]);
  const [doctorMappings, setDoctorMappings] = useState<SarvamDoctorMappingRow[]>([]);
  const [campaignProcedureMappings, setCampaignProcedureMappings] = useState<
    SarvamCampaignProcedureMappingRow[]
  >([]);

  const { data: savedMappings = [] } = useQuery<SarvamDispositionMappingRow[]>({
    queryKey: ["sarvam-disposition-mappings", agentAppId],
    queryFn: () => hospitalAdminApi.listSarvamDispositionMappings(agentAppId || undefined),
    enabled: !!agentAppId,
  });

  const { data: savedDoctorMappings = [] } = useQuery<SarvamDoctorMappingRow[]>({
    queryKey: ["sarvam-doctor-mappings", agentAppId],
    queryFn: () => hospitalAdminApi.listSarvamDoctorMappings(agentAppId || undefined),
    enabled: !!agentAppId,
  });

  const { data: savedCampaignProcedureMappings = [] } = useQuery<
    SarvamCampaignProcedureMappingRow[]
  >({
    queryKey: ["sarvam-campaign-procedure-mappings"],
    queryFn: () => hospitalAdminApi.listSarvamCampaignProcedureMappings(),
  });

  const { data: chainDoctors = [] } = useQuery<Doctor[]>({
    queryKey: ["hospital-admin-doctors"],
    queryFn: () => hospitalAdminApi.getDoctors(),
  });

  const activeDoctors = useMemo(
    () =>
      chainDoctors
        .filter((d) => d.status === "ACTIVE" && d.role === "DOCTOR")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [chainDoctors]
  );

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setRolloutMode(data.rolloutMode === 'LIVE' ? 'LIVE' : 'TEST');
    const crmAgent = pickCrmSarvamAgent(data.agents, data.defaultAgentId);
    setAgentEnabled(data.agentEnabled ?? crmAgent?.enabled ?? true);
    setLeadSources(data.leadSourcesAllowlist ?? []);
    setCampaigns(data.campaignNamesAllowlist ?? []);
    setStaffIds(data.assignedToStaffIdsAllowlist ?? []);
    setCallUnassigned(data.callUnassignedLeads ?? true);
    setCallDelayMinutes(String(data.callDelayMinutes ?? 0));
    const times = callingWindowTimesFromSettings(data.callingWindow);
    setStartTime(times.startTime);
    setEndTime(times.endTime);
    const agent = pickCrmSarvamAgent(data.agents, data.defaultAgentId);
    if (agent) {
      setAgentAppId(agent.sarvamAppId ?? data.defaultAgentId?.trim() ?? "");
      setAgentVersion(agent.sarvamAppVersion ?? "1");
      setConnectionId(agent.sarvamConnectionId ?? "");
      setAgentPhone(agent.agentPhoneNumber ?? "");
    } else if (data.defaultAgentId?.trim()) {
      setAgentAppId(data.defaultAgentId.trim());
    }
  }, [data]);

  useEffect(() => {
    setDispositionMappings(savedMappings);
  }, [savedMappings]);

  useEffect(() => {
    setDoctorMappings(savedDoctorMappings);
  }, [savedDoctorMappings]);

  useEffect(() => {
    setCampaignProcedureMappings(savedCampaignProcedureMappings);
  }, [savedCampaignProcedureMappings]);

  const allCampaignOptions = useMemo(() => {
    const set = new Set<string>([...campaignOptions, ...campaigns]);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [campaignOptions, campaigns]);

  const seedDoctorMutation = useMutation({
    mutationFn: () => hospitalAdminApi.seedSarvamDoctorMappings(agentAppId.trim() || undefined),
    onSuccess: async (result) => {
      toast.success(`Seeded ${result.created} doctor aliases (${result.skipped} skipped)`);
      await qc.invalidateQueries({ queryKey: ["sarvam-doctor-mappings"] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to seed doctor mappings";
      toast.error(message);
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => hospitalAdminApi.seedSarvamDispositionMappings(agentAppId.trim() || undefined),
    onSuccess: async (result) => {
      toast.success(`Seeded ${result.created} disposition rules (${result.skipped} already existed)`);
      await qc.invalidateQueries({ queryKey: ["sarvam-disposition-mappings"] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to seed disposition rules";
      toast.error(message);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const appId = agentAppId.trim();
      const agentForSave =
        data?.agents?.find((a) => a.sarvamAppId === appId) ??
        pickCrmSarvamAgent(data?.agents, data?.defaultAgentId);
      await hospitalAdminApi.saveSarvamVoiceAiSettings({
        enabled,
        agentEnabled,
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        sarvamOrgId: data?.sarvamOrgId,
        sarvamWorkspaceId: data?.sarvamWorkspaceId,
        defaultAgentId: agentAppId.trim() || undefined,
        callingWindow: {
          startTime,
          endTime,
        },
        leadSourcesAllowlist: leadSources,
        campaignNamesAllowlist: campaigns,
        assignedToStaffIdsAllowlist: staffIds,
        callUnassignedLeads: callUnassigned,
        callDelayMinutes: Number(callDelayMinutes) || 0,
        rolloutMode,
        agent: {
          id: agentForSave?.sarvamAppId === appId ? agentForSave.id : undefined,
          sarvamAppId: appId,
          sarvamAppVersion: agentVersion.trim() || "1",
          sarvamConnectionId: connectionId.trim(),
          agentPhoneNumber: agentPhone.trim(),
          name: agentForSave?.name || "CRM New Lead Agent",
          purpose: agentForSave?.purpose,
          enabled: agentEnabled,
        },
      });
      if (appId) {
        await hospitalAdminApi.saveSarvamDoctorMappings({
          sarvamAppId: appId,
          mappings: doctorMappings.filter(
            (m) => m.sarvamAlias.trim() && m.hospitalStaffId.trim()
          ),
        });
        await hospitalAdminApi.saveSarvamDispositionMappings({
          sarvamAppId: appId,
          mappings: dispositionMappings.filter(
            (m) => m.outputVariableKey.trim() && m.outputVariableValue.trim()
          ),
        });
      }
      await hospitalAdminApi.saveSarvamCampaignProcedureMappings({
        mappings: campaignProcedureMappings.filter(
          (m) => m.campaignPattern.trim() && m.procedureName.trim()
        ),
      });
    },
    onSuccess: async () => {
      toast.success("Sarvam Voice AI settings saved");
      setApiKey("");
      await qc.invalidateQueries({ queryKey: ["sarvam-voice-ai-settings"] });
      await qc.invalidateQueries({ queryKey: ["sarvam-disposition-mappings"] });
      await qc.invalidateQueries({ queryKey: ["sarvam-doctor-mappings"] });
      await qc.invalidateQueries({ queryKey: ["sarvam-campaign-procedure-mappings"] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to save Sarvam settings";
      toast.error(message);
    },
  });

  const toggleSource = (value: string, checked: boolean) => {
    setLeadSources((prev) =>
      checked ? [...new Set([...prev, value])] : prev.filter((v) => v !== value)
    );
  };

  const toggleCampaign = (name: string, checked: boolean) => {
    setCampaigns((prev) =>
      checked ? [...new Set([...prev, name])] : prev.filter((v) => v !== name)
    );
  };

  const toggleStaff = (id: string, checked: boolean) => {
    setStaffIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((v) => v !== id)
    );
  };

  const addCustomCampaign = () => {
    const v = customCampaign.trim();
    if (!v) return;
    setCampaigns((prev) => [...new Set([...prev, v])]);
    setCustomCampaign("");
  };

  const addDoctorMappingRow = () => {
    setDoctorMappings((prev) => [
      ...prev,
      {
        sarvamAlias: "",
        hospitalStaffId: activeDoctors[0]?.id ?? "",
        priority: 100,
        enabled: true,
      },
    ]);
  };

  const updateDoctorMappingRow = (index: number, patch: Partial<SarvamDoctorMappingRow>) => {
    setDoctorMappings((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addCampaignProcedureMappingRow = () => {
    setCampaignProcedureMappings((prev) => [
      ...prev,
      { campaignPattern: "", procedureName: "", priority: 100, enabled: true },
    ]);
  };

  const updateCampaignProcedureMappingRow = (
    index: number,
    patch: Partial<SarvamCampaignProcedureMappingRow>
  ) => {
    setCampaignProcedureMappings((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const addDispositionRow = () => {
    setDispositionMappings((prev) => [
      ...prev,
      {
        outputVariableKey: "disposition",
        outputVariableValue: "",
        targetStatus: "INTERESTED",
        targetSubStatus: "WANTS_CALL_BACK",
        priority: 100,
        enabled: true,
      },
    ]);
  };

  const updateDispositionRow = (
    index: number,
    patch: Partial<SarvamDispositionMappingRow>
  ) => {
    setDispositionMappings((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sarvam Voice AI — Outbound Agent</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Automatically place Sarvam instant outbound calls when new CRM leads are ingested.
          Telephony runs through Sarvam (no separate Exotel setup required for this feature).
          Calls only run during the configured IST window and when all filters below match.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Checkbox
              checked={enabled}
              onCheckedChange={(v) => setEnabled(v === true)}
              disabled={isLoading || saveMutation.isPending}
            />
            <div>
              <Label className="font-medium">Integration ON</Label>
              <div className="text-xs text-muted-foreground mt-1">
                Master switch — allows auto-calls when new leads match your filters.
                {data?.hasApiKey ? " API key configured." : " API key not saved yet."}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Checkbox
              checked={agentEnabled}
              onCheckedChange={(v) => setAgentEnabled(v === true)}
              disabled={isLoading || saveMutation.isPending || !enabled}
            />
            <div>
              <Label className="font-medium">Agent ON</Label>
              <div className="text-xs text-muted-foreground mt-1">
                Pause the voice agent without removing API credentials or filter rules.
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border p-3 space-y-2 bg-amber-50/80 border-amber-200">
          <Label className="font-medium">Rollout mode</Label>
          <Select
            value={rolloutMode}
            onValueChange={(v) => setRolloutMode(v === 'LIVE' ? 'LIVE' : 'TEST')}
            disabled={isLoading || saveMutation.isPending}
          >
            <SelectTrigger className="max-w-md bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TEST">Test — only leads with &quot;Test&quot; in patient name</SelectItem>
              <SelectItem value="LIVE">Live — all leads matching filters below</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {rolloutMode === 'TEST'
              ? 'Safe default after deploy. Example: "Rohit Test Lead" or "Test Patient" will receive calls; real patient names will not.'
              : 'Production mode — every new lead that passes source, campaign, and staff filters will receive an outbound call.'}
          </p>
          {enabled && rolloutMode === 'LIVE' && (
            <p className="text-xs text-amber-800 font-medium">
              Live mode is ON — real CRM leads will be called when ingested.
            </p>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Voice Agents API key</Label>
            <Input
              type="password"
              placeholder={data?.hasApiKey ? "Leave blank to keep existing key" : "sk_samvaad_…"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={saveMutation.isPending}
            />
          </div>
          <div className="space-y-1">
            <Label>Call delay after ingest (minutes)</Label>
            <Input
              type="number"
              min={0}
              value={callDelayMinutes}
              onChange={(e) => setCallDelayMinutes(e.target.value)}
              disabled={saveMutation.isPending}
            />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 border rounded-lg p-3 bg-muted/20">
          <div className="space-y-1 md:col-span-2">
            <Label className="font-medium">Agent connection (Sarvam telephony)</Label>
          </div>
          <div className="space-y-1">
            <Label>Agent ID (app_id)</Label>
            <Input value={agentAppId} onChange={(e) => setAgentAppId(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Agent version</Label>
            <Input value={agentVersion} onChange={(e) => setAgentVersion(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Connection ID</Label>
            <Input value={connectionId} onChange={(e) => setConnectionId(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Agent phone (E.164)</Label>
            <Input value={agentPhone} onChange={(e) => setAgentPhone(e.target.value)} />
          </div>
        </div>

        <div className="rounded-lg border bg-muted/10 p-3 space-y-2 text-sm">
          <Label className="font-medium">Variable integration (THMS ↔ Sarvam)</Label>
          <div>
            <span className="font-medium text-xs uppercase text-muted-foreground">
              Inputs (full contract)
            </span>
            <p className="text-xs text-muted-foreground mt-1">
              {(data?.inputVariableContract ?? []).join(", ")}
            </p>
            {(data?.submitInputKeys?.length ?? 0) > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                <span className="font-medium">Currently submitted:</span>{" "}
                {(data?.submitInputKeys ?? []).join(", ")}
              </p>
            )}
            {(data?.inputVariableHints?.length ?? 0) > 0 && (
              <ul className="text-xs text-muted-foreground mt-1 list-disc pl-4 space-y-0.5">
                {(data?.inputVariableHints ?? []).map((hint) => (
                  <li key={hint}>{hint}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <span className="font-medium text-xs uppercase text-muted-foreground">Outputs expected</span>
            <ul className="text-xs text-muted-foreground mt-1 list-disc pl-4 space-y-0.5">
              {(data?.outputVariableHints ?? []).map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </ul>
          </div>
          {data?.liveToolEndpoints && (
            <div>
              <span className="font-medium text-xs uppercase text-muted-foreground">
                Live tools (configure in Sarvam → Tools)
              </span>
              <ul className="text-xs text-muted-foreground mt-1 list-disc pl-4 space-y-0.5 font-mono break-all">
                <li>List doctors: {data.liveToolEndpoints.listDoctors}</li>
                <li>List slots: {data.liveToolEndpoints.listSlots}</li>
                <li>Book OPD: {data.liveToolEndpoints.bookAppointment}</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-1">
                Auth header: <code>{data.toolsAuthHeader ?? "X-Sarvam-Tools-Key"}</code> — use the same
                value as <code>SARVAM_TOOLS_API_KEY</code> on the server (or Voice Agents API key).
                Pass <code>leadId</code> from input variable <code>user_identifier</code> in every tool call.
              </p>
            </div>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Calling window start (IST)</Label>
            <Input
              type="time"
              step={60}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={saveMutation.isPending}
            />
          </div>
          <div className="space-y-1">
            <Label>Calling window end (IST, inclusive)</Label>
            <Input
              type="time"
              step={60}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={saveMutation.isPending}
            />
          </div>
          <p className="text-xs text-muted-foreground md:col-span-2">
            Default is 00:00–23:59 IST (calls allowed all day). Times use India Standard Time, not
            your browser timezone.
          </p>
        </div>

        <div className="space-y-2 border-t pt-4">
          <Label className="font-medium">Lead sources to call</Label>
          <p className="text-xs text-muted-foreground">
            Only checked sources will receive an outbound voice call on ingest.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 max-h-48 overflow-y-auto border rounded-md p-3">
            {CRM_LEAD_SOURCES.map((s) => (
              <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={leadSources.includes(s.value)}
                  onCheckedChange={(v) => toggleSource(s.value, v === true)}
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
          {enabled && leadSources.length === 0 && (
            <p className="text-xs text-amber-700">Select at least one lead source before enabling calls.</p>
          )}
        </div>

        <div className="space-y-2 border-t pt-4">
          <Label className="font-medium">Campaigns to call</Label>
          <p className="text-xs text-muted-foreground">
            Leave all unchecked to call every campaign. Check specific names to restrict (matches
            lead campaign name or Google UTM campaign).
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="Add custom campaign name"
              value={customCampaign}
              onChange={(e) => setCustomCampaign(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustomCampaign()}
            />
            <Button type="button" variant="outline" onClick={addCustomCampaign}>
              Add
            </Button>
          </div>
          {allCampaignOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No campaign names found in CRM yet.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 max-h-40 overflow-y-auto border rounded-md p-3">
              {allCampaignOptions.map((name) => (
                <label key={name} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={campaigns.includes(name)}
                    onCheckedChange={(v) => toggleCampaign(name, v === true)}
                  />
                  <span className="truncate" title={name}>
                    {name}
                  </span>
                </label>
              ))}
            </div>
          )}
          {campaigns.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setCampaigns([])}>
              Clear campaign filter (call all campaigns)
            </Button>
          )}
        </div>

        <div className="space-y-2 border-t pt-4">
          <Label className="font-medium">Sales persons — whose leads to call</Label>
          <p className="text-xs text-muted-foreground">
            Leave all unchecked to call leads owned by any salesperson. Check names to restrict to
            those owners only.
          </p>
          <div className="flex items-center gap-2 mb-2">
            <Checkbox
              checked={callUnassigned}
              onCheckedChange={(v) => setCallUnassigned(v === true)}
              disabled={staffIds.length === 0}
            />
            <span className="text-sm text-muted-foreground">
              Also call unassigned leads (when a staff filter is active)
            </span>
          </div>
          {salesAssignees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No in-house sales persons found.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 max-h-40 overflow-y-auto border rounded-md p-3">
              {salesAssignees.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={staffIds.includes(u.id)}
                    onCheckedChange={(v) => toggleStaff(u.id, v === true)}
                  />
                  <span>{u.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                </label>
              ))}
            </div>
          )}
          {staffIds.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setStaffIds([])}>
              Clear staff filter (all owners)
            </Button>
          )}
        </div>

        <div className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="font-medium">CRM campaign → surgery (Sarvam voice)</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCampaignProcedureMappingRow}
            >
              Add mapping
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            When a lead&apos;s campaign name contains the pattern (case-insensitive), THMS sends
            that surgery as <code className="text-xs">procedure_name</code> /{" "}
            <code className="text-xs">service_name</code> on outbound Sarvam calls. Auto-detection
            still runs for common names (Rhinoplasty, Liposuction, …); use this for new Google Ads
            codes like <span className="font-mono">GGQ-004-Bariatric-Surgery-Gurgaon</span>.
          </p>
          {campaignProcedureMappings.length === 0 ? (
            <p className="text-sm text-muted-foreground border rounded-md p-3">
              No campaign mappings yet. Add a row when Sarvam should speak a surgery that is not
              parsed automatically from the campaign string.
            </p>
          ) : (
            <div className="space-y-2">
              {campaignProcedureMappings.map((row, index) => (
                <div
                  key={`${row.id ?? "new"}-camp-${index}`}
                  className="grid gap-2 md:grid-cols-5 border rounded-md p-3 text-sm items-center"
                >
                  <Select
                    value={row.campaignPattern || "__custom__"}
                    onValueChange={(v) => {
                      if (v === "__custom__") return;
                      updateCampaignProcedureMappingRow(index, { campaignPattern: v });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Campaign contains…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__custom__">Type or pick campaign…</SelectItem>
                      {campaignOptions.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Campaign substring (e.g. Bariatric-Surgery)"
                    value={row.campaignPattern}
                    onChange={(e) =>
                      updateCampaignProcedureMappingRow(index, { campaignPattern: e.target.value })
                    }
                    list="sarvam-campaign-suggestions"
                  />
                  <Input
                    placeholder="Surgery for Sarvam (e.g. Bariatric Surgery)"
                    value={row.procedureName}
                    onChange={(e) =>
                      updateCampaignProcedureMappingRow(index, { procedureName: e.target.value })
                    }
                    list="sarvam-procedure-suggestions"
                  />
                  <Input
                    type="number"
                    className="w-full"
                    placeholder="Priority"
                    value={String(row.priority ?? 100)}
                    onChange={(e) =>
                      updateCampaignProcedureMappingRow(index, {
                        priority: Number(e.target.value) || 100,
                      })
                    }
                  />
                  <div className="flex items-center gap-2 justify-end">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={row.enabled !== false}
                        onCheckedChange={(v) =>
                          updateCampaignProcedureMappingRow(index, { enabled: v === true })
                        }
                      />
                      On
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setCampaignProcedureMappings((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <datalist id="sarvam-procedure-suggestions">
            {SARVAM_PROCEDURE_NAME_SUGGESTIONS.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>

        <div className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="font-medium">Sarvam doctor name → SetuOS doctor</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => seedDoctorMutation.mutate()}
                disabled={seedDoctorMutation.isPending || !agentAppId.trim()}
              >
                {seedDoctorMutation.isPending ? "Seeding…" : "Seed legacy cosmetic doctors"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addDoctorMappingRow}>
                Add alias
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            When Sarvam sends a doctor name in <code>doctor_name</code>, <code>call_summary</code>, or
            similar fields, the first matching alias (by priority) maps to the SetuOS doctor used for
            OPD auto-booking. Scoped to the Agent App ID above; empty app id rows apply to all agents
            in the chain.
          </p>
          {doctorMappings.length === 0 ? (
            <p className="text-sm text-muted-foreground border rounded-md p-3">
              No doctor aliases yet — add names exactly as Sarvam speaks or prints them (e.g.{" "}
              <span className="font-mono">Dr Raman</span>, <span className="font-mono">raman</span>).
            </p>
          ) : (
            <div className="space-y-2">
              {doctorMappings.map((row, index) => (
                <div
                  key={`${row.id ?? "new"}-${index}`}
                  className="grid gap-2 md:grid-cols-5 border rounded-md p-3 text-sm items-center"
                >
                  <Input
                    placeholder="Sarvam alias (text match)"
                    value={row.displayAlias ?? row.sarvamAlias}
                    onChange={(e) =>
                      updateDoctorMappingRow(index, {
                        sarvamAlias: e.target.value,
                        displayAlias: e.target.value,
                      })
                    }
                  />
                  <Select
                    value={row.hospitalStaffId || "__none__"}
                    onValueChange={(v) =>
                      updateDoctorMappingRow(index, {
                        hospitalStaffId: v === "__none__" ? "" : v,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="SetuOS doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select doctor</SelectItem>
                      {activeDoctors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                          {d.specialisation ? ` · ${d.specialisation}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    className="w-full"
                    placeholder="Priority"
                    value={String(row.priority ?? 100)}
                    onChange={(e) =>
                      updateDoctorMappingRow(index, { priority: Number(e.target.value) || 100 })
                    }
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={row.enabled !== false}
                      onCheckedChange={(v) =>
                        updateDoctorMappingRow(index, { enabled: v === true })
                      }
                    />
                    Enabled
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="justify-self-end"
                    onClick={() => setDoctorMappings((prev) => prev.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="font-medium">Output → CRM disposition mapping</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => seedMutation.mutate()}
                disabled={seedMutation.isPending || !agentAppId.trim()}
              >
                {seedMutation.isPending ? "Seeding…" : "Seed cosmetic agent rules"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addDispositionRow}>
                Add rule
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Maps Sarvam output variables (<code>call_disposition</code>, <code>sub_status</code>,{" "}
            <code>booked_appointment</code>) to CRM status/sub-status. Use &quot;Seed cosmetic agent
            rules&quot; for the default Cosmetic Clinic Lead Outbound agent.
          </p>
          {dispositionMappings.length === 0 ? (
            <p className="text-sm text-muted-foreground border rounded-md p-3">
              No disposition rules yet — add rules matching your Sarvam agent output variables.
            </p>
          ) : (
            <div className="space-y-2">
              {dispositionMappings.map((row, index) => (
                <div
                  key={`${row.outputVariableKey}-${index}`}
                  className="grid gap-2 md:grid-cols-6 border rounded-md p-3 text-sm"
                >
                  <Input
                    placeholder="Output key"
                    value={row.outputVariableKey}
                    onChange={(e) =>
                      updateDispositionRow(index, { outputVariableKey: e.target.value })
                    }
                  />
                  <Input
                    placeholder="Output value"
                    value={row.outputVariableValue}
                    onChange={(e) =>
                      updateDispositionRow(index, { outputVariableValue: e.target.value })
                    }
                  />
                  <Select
                    value={row.targetStatus ?? "__none__"}
                    onValueChange={(v) =>
                      updateDispositionRow(index, {
                        targetStatus: v === "__none__" ? null : v,
                        targetSubStatus: null,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No status</SelectItem>
                      {LEAD_STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {formatLeadStatusLabel(s)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={row.targetSubStatus ?? "__none__"}
                    onValueChange={(v) =>
                      updateDispositionRow(index, {
                        targetSubStatus: v === "__none__" ? null : v,
                      })
                    }
                    disabled={!row.targetStatus}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sub-status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No sub-status</SelectItem>
                      {(row.targetStatus
                        ? LEAD_SUBSTATUS_OPTIONS_BY_STATUS[row.targetStatus as LeadStatus]
                        : []
                      ).map((ss) => (
                        <SelectItem key={ss} value={ss}>
                          {formatLeadSubStatusLabel(ss as LeadSubStatus)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="md:col-span-2 justify-self-end"
                    onClick={() =>
                      setDispositionMappings((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t pt-4">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={isLoading || saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving…" : "Save Sarvam settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
